import { Card } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { AsyncStatus } from 'features/files/fincen-reporting/constants';
import Validation from 'features/files/order-insights/components/Validation';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import FormDrawerComponent from 'ui-kit/components/drawer/FormDrawerComponent';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import { partyFileInfoValidation } from 'utilities/validation/schemas';
import { doValidate, FieldValidationError } from 'utilities/validation/validation';
import DrawerFileSection, { underWriterOptions } from './DrawerFileSection';
import { PartyVerificationDrawerProps } from './interfaces/PartyDrawer';
import { Party } from 'api/file/interfaces/get-file-order-insights';
import { useFirmUnderwriterOptions } from './useFirmUnderwriterOptions';
import { UnderwriterProductStatus, useUnderwriterProductStatus } from './useUnderwriterProductStatus';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
      width: '100%',
    },
    newPartyContainer: {
      marginTop: theme.spacing(1.5),
    },
    customCard: {
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: 'none',
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '50px',
    },
  }),
);

// Partial party data for initialization
// missing fields only use in order insight
const defaultData = {
  address: {
    isForeignAddress: false,
    address1: '',
    address2: '',
    city: '',
    postalCode: '',
    stateOrProvince: '',
  },
  phoneNumber: '',
  emailAddress: '',
} as Party;

const PartyVerificationDrawer: React.FC<PartyVerificationDrawerProps> = ({
  party,
  isNewParty,
  openDrawer,
  closeDrawer,
  marketPlace,
  enableStatus,
}) => {
  const classNames = useStyles();
  const [drawerValue, setDrawerValue] = useState<Party>(defaultData);
  const {
    options: firmUnderwriterOptions,
    isLoading: isLoadingUnderwriters,
    isNonFaUnderwriter,
  } = useFirmUnderwriterOptions(openDrawer);

  const [isPersonalIdentificationFormGroupValid, setIsPersonalIdentificationFormGroupValid] = useState<boolean>(false);
  const [showAllValidation, setShowAllValidation] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<FieldValidationError[]>([]);

  const validateFileErrors = async (currentDetails?: Partial<Party>) => {
    const errs =
      (await doValidate(
        {
          underwriterCode: currentDetails?.underwriterCode ?? drawerValue.underwriterCode,
          fileNumber: currentDetails?.fileNumber ?? drawerValue.fileNumber,
        },
        partyFileInfoValidation,
      )) ?? [];

    setValidationErrors(errs);
    return isPersonalIdentificationFormGroupValid && errs.length === 0;
  };

  const handleDrawerFileChange = (updatedValue?: Partial<Party>) => {
    setDrawerValue((prevValue: Party) => ({
      ...prevValue,
      ...updatedValue,
    }));
    validateFileErrors(updatedValue);
  };

  const showValidation = useCallback(() => {
    setShowAllValidation(true);
  }, []);

  const resetStateAndCloseDrawer = () => {
    const shouldRefetch =
      marketPlace.validateContactInfoStatus === AsyncStatus.SUCCESS ||
      marketPlace.enhancedIdentityVerificationStatus === AsyncStatus.SUCCESS ||
      marketPlace.cancelPartyVerificationStatus === AsyncStatus.SUCCESS;

    setDrawerValue(defaultData);
    setValidationErrors([]);
    setShowAllValidation(false);
    closeDrawer();
    // Reset marketplace verification data using proper reset functions
    marketPlace.resetAllVerificationData();

    if (shouldRefetch) {
      marketPlace.fetchPartyVerificationList();
    }
  };

  const getPartyData = useCallback(() => {
    // For existing parties, use drawerValue
    if (!isNewParty) {
      return {
        firstName: drawerValue.personalInformation?.firstName || '',
        lastName: drawerValue.personalInformation?.lastName || '',
        fileNumber: drawerValue.fileNumber || '',
        underwriterCode: drawerValue.underwriterCode || '',
      };
    }

    // For new parties, check results after submission
    const results =
      marketPlace.validateContactInfoStatusResults ?? marketPlace.enhancedIdentityVerificationStatusResults;
    if (results?.submittedData) {
      return {
        firstName: results.submittedData.firstName || '',
        lastName: results.submittedData.lastName || '',
        fileNumber: results.submittedData.fileNumber || '',
        underwriterCode: results.submittedData.underwriter || '',
      };
    }

    return null;
  }, [
    isNewParty,
    drawerValue,
    marketPlace.validateContactInfoStatusResults,
    marketPlace.enhancedIdentityVerificationStatusResults,
  ]);

  const title = useMemo(() => {
    const data = getPartyData();
    if (data && (data.firstName || data.lastName)) {
      const name = [data.firstName, data.lastName].filter(Boolean).join(' ');
      return `Party Verification: ${name}`;
    }
    return 'New Party Verification';
  }, [getPartyData]);

  // The underwriter is picked in the new party flow and carried on the file for an existing one,
  // so both are gated the same way off whichever underwriter the drawer is working with.
  const isNonFaUnderwriterSelected = isNonFaUnderwriter(drawerValue.underwriterCode);

  const { isFeatureEnabled, productStatus } = useUnderwriterProductStatus(drawerValue.underwriterCode, openDrawer);

  // Enablement follows the selected underwriter rather than the firm level marketplace status.
  // Until an underwriter is picked the products stay open, so the form is never blocked up front.
  const isProductEnabled = (product: keyof UnderwriterProductStatus, firmLevelEnabled: boolean) => {
    if (!isFeatureEnabled) {
      return firmLevelEnabled;
    }

    if (!drawerValue.underwriterCode) {
      return true;
    }

    return productStatus?.[product] ?? true;
  };

  const subTitle = useMemo(() => {
    const data = getPartyData();
    if (data && (data.underwriterCode || data.fileNumber)) {
      const options = firmUnderwriterOptions ?? underWriterOptions;
      return `Underwriter: ${
        options.find((option) => option.value === data.underwriterCode)?.name || ''
      } | File Number: ${data.fileNumber}`;
    }
    return '';
  }, [getPartyData, firmUnderwriterOptions]);

  useEffect(() => {
    if (typeof party === 'object' && Object.keys(party).length > 0 && !isNewParty) {
      setDrawerValue({
        ...party,
        personalInformation: {
          firstName: party.firstName,
          lastName: party.lastName,
        },
      });
      // in case of existing file check if the required field is there are not
      // as this will be hidden in existing file so no need to do extensive validation
      const maxNameLength = 400;
      const hasRequiredFields = !!(
        party.firstName &&
        party.lastName &&
        party.firstName.length <= maxNameLength &&
        party.lastName.length <= maxNameLength
      );
      setIsPersonalIdentificationFormGroupValid(hasRequiredFields);
    }
  }, [party, isNewParty]);

  return (
    <FormDrawerComponent
      title={title}
      id="party-verification-drawer-body"
      subTitle={subTitle}
      open={openDrawer}
      width={960}
      disableEnforceFocus
      showDrawerActions={false}
      onDismissAction={resetStateAndCloseDrawer}
      crossQAPrefix="new-verification-button-"
    >
      {marketPlace.getPartyVerificationFileDetailStatus === 'pending' ? (
        <div className={classNames.loadingContainer}>
          <LoadingSpinner status="pending" />
        </div>
      ) : (
        <div className={classNames.container}>
          {isNewParty && (
            <DrawerFileSection
              value={drawerValue}
              onChange={handleDrawerFileChange}
              validationErrors={validationErrors}
              setIsPersonalIdentificationFormGroupValid={setIsPersonalIdentificationFormGroupValid}
              showAllValidation={showAllValidation}
              underwriterOptions={firmUnderwriterOptions}
              isLoadingUnderwriters={isLoadingUnderwriters}
            />
          )}
          <div className={`${classNames.container} ${!isNewParty ? classNames.newPartyContainer : ''}`}>
            {enableStatus?.contactValidation !== undefined && (
              <Card className={classNames.customCard}>
                <Validation
                  party={drawerValue}
                  hasRisks={false}
                  title="Contact Validation"
                  isDrawer
                  qaPrefix="new-verification-contact-validation"
                  index={0}
                  updatedPartyInfo={handleDrawerFileChange}
                  description={enableStatus.contactValidation.description}
                  isEnabled={isProductEnabled('contactValidation', enableStatus.contactValidation.enabled)}
                  productCode={enableStatus.contactValidation.productCode}
                  marketPlace={marketPlace}
                  validateFileErrors={validateFileErrors}
                  showValidation={showValidation}
                  isNonFaUnderwriter={isNonFaUnderwriterSelected}
                  alwaysShowForm={isFeatureEnabled}
                />
              </Card>
            )}
            {enableStatus?.enhancedIdentityVerification !== undefined && (
              <Card className={classNames.customCard}>
                <Validation
                  party={drawerValue}
                  marketPlace={marketPlace}
                  hasRisks={false}
                  title="Identity Verification"
                  type="EnhancedIdentityVerification"
                  description={enableStatus.enhancedIdentityVerification.description}
                  isEnabled={isProductEnabled(
                    'enhancedIdentityVerification',
                    enableStatus.enhancedIdentityVerification.enabled,
                  )}
                  productCode={enableStatus.enhancedIdentityVerification.productCode}
                  qaPrefix="new-verification"
                  isDrawer
                  index={0}
                  updatedPartyInfo={handleDrawerFileChange}
                  validateFileErrors={validateFileErrors}
                  showValidation={showValidation}
                  isPartyVerification={true}
                  isNonFaUnderwriter={isNonFaUnderwriterSelected}
                  alwaysShowForm={isFeatureEnabled}
                />
              </Card>
            )}
          </div>
        </div>
      )}
    </FormDrawerComponent>
  );
};

export default PartyVerificationDrawer;
